import React, { useState, useEffect, useMemo } from 'react';
import { Button, Card, Row, Col, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { Loading } from '@rd-web-markets/shared/dist/util';
import claimSubcontractorsService from '../../services/claim/claim_subcontractors.service';
import { handleError } from '@rd-web-markets/shared/dist/store/features/alertSlice';
import { useDispatch, useSelector } from 'react-redux';
import { SubcontractorRow, SubcontractorRowHeader, SubcontractorTotalsRow } from './';
import NotesList from '@rd-web-markets/shared/dist/financialSection/NotesList';
import ReactDataGridCostFragmentsTable from '../costFragments/ReactDataGridCostFragmentsTable';
import AddNoteButton from '../AddNoteButton';
export const SubcontractorsSection = _ref => {
  let {
    accountType,
    claim,
    refreshClaim,
    setClaim,
    showCostFragmentsTable,
    allProjects
  } = _ref;
  const [claimSubcontractors, setClaimSubcontractors] = useState(null);
  const [headerScrollPosition, setHeaderScrollPosition] = useState(0);
  const dispatch = useDispatch();
  const user = useSelector(state => state.auth.user);
  const claimId = useMemo(() => claim?.id, [claim]);
  const [isShowInvolvement, setIsShowInvolvement] = useState(null);
  useEffect(() => {
    async function getData() {
      try {
        if (claimId) {
          setClaimSubcontractors(await claimSubcontractorsService.all(claimId));
        }
      } catch (error) {
        dispatch(handleError(error));
      }
    }
    setIsShowInvolvement(claim.claim_group.cost_methodology === 'consolidated');
    getData();
  }, [dispatch, claimId]);
  const handleDelete = async index => {
    const subcontractor = claimSubcontractors[index];
    if (subcontractor.id) {
      try {
        await claimSubcontractorsService.delete(subcontractor);
        const subcontractors = await claimSubcontractorsService.all(claim.id);
        setClaimSubcontractors(subcontractors);
        refreshClaim();
      } catch (error) {
        dispatch(handleError(error));
      }
    } else {
      const newSubcontractors = [...claimSubcontractors];
      newSubcontractors.splice(index, 1);
      setClaimSubcontractors(newSubcontractors);
    }
  };
  const handleAdd = subcontractor => {
    setClaimSubcontractors([...claimSubcontractors, subcontractor]);
  };
  const sortedClaimProjectReports = useMemo(() => {
    if (!allProjects) return [];
    return allProjects.sort((a, b) => {
      if (a.project_name < b.project_name) {
        return -1;
      }
      if (a.project_name > b.project_name) {
        return 1;
      }
      return 0;
    });
  }, [claim]);
  if (!claim || !claimSubcontractors) return /*#__PURE__*/React.createElement(Loading, null);
  const hasPermissions = () => {
    return user.account_type !== 'customer' && user.account_type !== 'accountant';
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "d-flex justify-content-end align-items-center gap-2 mb-3"
  }, /*#__PURE__*/React.createElement(AddNoteButton, {
    claimId: claim.id,
    category: "subcontractors",
    userCanEdit: hasPermissions() && !claim.financials_locked && !claim.is_locked
  })), !showCostFragmentsTable && /*#__PURE__*/React.createElement(React.Fragment, null, claimSubcontractors.length > 0 ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Table, {
    size: "sm",
    className: "pt-4"
  }, /*#__PURE__*/React.createElement("thead", {
    className: "text-center"
  }, /*#__PURE__*/React.createElement(SubcontractorRowHeader, {
    claim: claim,
    setHeaderScrollPosition: setHeaderScrollPosition,
    isShowInvolvement: isShowInvolvement,
    allProjects: allProjects
  })), /*#__PURE__*/React.createElement("tbody", null, claimSubcontractors.map((subcontractor, index) => {
    return /*#__PURE__*/React.createElement(SubcontractorRow, {
      key: index,
      index: index,
      claim: claim,
      sortedClaimProjectReports: sortedClaimProjectReports,
      headerScrollPosition: headerScrollPosition,
      handleDelete: () => handleDelete(index),
      claimSubcontractors: claimSubcontractors,
      setClaimSubcontractors: setClaimSubcontractors,
      refreshClaim: refreshClaim,
      handleError: handleError,
      claimSubcontractorsService: claimSubcontractorsService,
      isShowInvolvement: isShowInvolvement
    });
  }), /*#__PURE__*/React.createElement(SubcontractorTotalsRow, {
    claim: claim,
    isShowInvolvement: isShowInvolvement
  })))) : /*#__PURE__*/React.createElement(Row, {
    className: "pt-5 pb-5 text-center"
  }, /*#__PURE__*/React.createElement(Col, null, /*#__PURE__*/React.createElement("h3", {
    className: "text-center text-muted"
  }, "No data"))), hasPermissions() && !showCostFragmentsTable && /*#__PURE__*/React.createElement(Row, {
    className: "mt-4"
  }, /*#__PURE__*/React.createElement(Col, null, /*#__PURE__*/React.createElement(Button, {
    variant: "link",
    onClick: () => handleAdd({
      claim_id: claim.id,
      claim_employee_rnd_apportionments: [],
      net_amount: 0.00
    }),
    disabled: claim.is_locked || claim.financials_locked || claim.active_change_set
  }, /*#__PURE__*/React.createElement("span", {
    className: "material-icons md-18"
  }, "add"), " Add Row")))), showCostFragmentsTable && /*#__PURE__*/React.createElement(ReactDataGridCostFragmentsTable, {
    claim: claim,
    refreshClaim: refreshClaim,
    costFragmentType: "UkSection::ClaimSubcontractor",
    userUnauthorizedToEdit: !hasPermissions(),
    allProjects: allProjects
  }), /*#__PURE__*/React.createElement(Row, null, /*#__PURE__*/React.createElement(Col, {
    md: 6,
    className: "text-start"
  }, /*#__PURE__*/React.createElement(Button, {
    as: Link,
    variant: "outline-primary",
    to: `/${accountType}/claims/${claim.id}/financial_details/staffing_costs`
  }, /*#__PURE__*/React.createElement("span", {
    className: "material-icons md-18"
  }, "chevron_left"), " Back")), /*#__PURE__*/React.createElement(Col, {
    md: 6,
    className: "text-end"
  }, /*#__PURE__*/React.createElement(Button, {
    as: Link,
    variant: "primary",
    to: `/${accountType}/claims/${claim.id}/financial_details/external_workers`
  }, "Continue ", /*#__PURE__*/React.createElement("span", {
    className: "material-icons md-18"
  }, "chevron_right")))));
};